import { logger } from "@/lib/logger";
import React, { useEffect, Suspense } from "react";

import { SiteLabelMetaSync } from "@/components/pwa/SiteLabelMetaSync";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { ProductionOptimizations } from "@/components/optimization/ProductionOptimizations";
import { ErrorBoundary } from "@/components/optimization/ErrorBoundary";
import { ScrollToTop } from "@/components/routing/ScrollToTop";
import { DefaultSEO } from "@/components/seo/DefaultSEO";
import { useLanguagePersistence } from "@/hooks/useLanguagePersistence";

function LanguagePersistence() {
  useLanguagePersistence();
  return null;
}
import { captureAndPersistUTM } from '@/utils/utmTracking';
import { DataSaverProvider } from "@/contexts/DataSaverContext";
import { Skeleton } from '@/components/ui/skeleton';
import { useOfflineFileQueueProcessor } from "@/hooks/useOfflineFileQueueProcessor";
import { initializeAutoSync } from "@/utils/offlineFormSync";
import { initSyncOrchestrator, registerSyncTask } from "@/lib/syncOrchestrator";
import { offlineQueue } from "@/lib/offlineQueue";
import { processOfflineQueue } from "@/utils/offlineEdgeFunctions";
import { syncPHCData } from "@/lib/phcSyncService";

// Modular route definitions
import { publicRoutes } from "@/routes/public.routes";
import { protectedRoutes } from "@/routes/protected.routes";
import { adminRoutes } from "@/routes/admin.routes";
import { regionalRoutes } from "@/routes/regional.routes";
import { professionalRoutes } from "@/routes/professional.routes";
import { resourceRoutes } from "@/routes/resource.routes";
import { serpRoutes } from "@/routes/serp.routes";
import { redirects } from "@/routes/redirects.map";
import { umojaregRedirects, buildUmojaregUrl } from "@/routes/umojareg.redirects";
import ExternalRedirect from "@/components/redirects/ExternalRedirect";

// Special pages
import NotFound from "./pages/NotFound";
import Logout from "./pages/Logout";
import VanityInviteRedirect from "./components/redirects/VanityInviteRedirect";
import { PHCHelpMount } from "@/components/help/PHCHelpMount";

// Helper to render routes from route objects
function renderRoutes(routes: any[]) {
  return routes.map((route, index) => (
    <Route key={route.path || index} path={route.path} element={route.element} />
  ));
}

// Helper to render redirect routes
function renderRedirects() {
  return redirects.map((redirect) => (
    <Route
      key={redirect.from}
      path={redirect.from}
      element={<Navigate to={redirect.to} replace={redirect.replace !== false} />}
    />
  ));
}

// BRIDGE SERP split — cross-origin redirects to UmojaReg
function renderUmojaregRedirects() {
  return umojaregRedirects.map((rule) => {
    if (rule.prefix) {
      const basePath = rule.from.replace(/\*$/, "");
      return (
        <Route
          key={rule.from}
          path={`${basePath}*`}
          element={<ExternalRedirect to={buildUmojaregUrl(rule)} />}
        />
      );
    }
    return (
      <Route
        key={rule.from}
        path={rule.from}
        element={<ExternalRedirect to={buildUmojaregUrl(rule)} />}
      />
    );
  });
}

const App = () => {
  // Single performance monitoring instance
  usePerformanceMonitor();
  
  // Initialize offline queue processor
  useOfflineFileQueueProcessor();
  
  useEffect(() => {
    // Capture and persist UTM parameters on every page load
    captureAndPersistUTM();
    
    // Initialize unified sync orchestrator — stagers all sync tasks on reconnect
    // Priority 1 (highest): Core request queue (most likely to contain user-initiated actions)
    registerSyncTask('offlineQueue', () => offlineQueue.processQueue(), { priority: 1, delayMs: 1500 });
    // Priority 2: Edge function calls
    registerSyncTask('offlineEdgeFunctions', () => processOfflineQueue(), { priority: 2, delayMs: 2000 });
    // Priority 3: PHC clinical data (consultations, referrals)
    registerSyncTask('phcSync', () => syncPHCData().then(() => {}), { priority: 3, delayMs: 3000 });
    // Priority 4: Field Worker clinical data (vitals, medical info, labs, consent)
    registerSyncTask('fieldWorkerSync', async () => {
      const { syncFieldWorkerData } = await import("@/lib/fieldWorkerSyncService");
      await syncFieldWorkerData();
    }, { priority: 4, delayMs: 3500 });
    // Priority 5: eCRF form submissions
    registerSyncTask('ecrfFormSync', async () => {
      const { syncOfflineFormSubmissions } = await import("@/utils/offlineFormSync");
      await syncOfflineFormSubmissions(true);
    }, { priority: 5, delayMs: 4000 });
    // Priority 6: Patient self-triage queued submissions (idempotent on submission_uuid)
    (async () => {
      const { initPatientSelfTriageReplay } = await import("@/lib/patientSelfTriageQueue");
      initPatientSelfTriageReplay();
    })();
    // Priority 5/6: Health-passport-record + voice-consent retry queues
    // (AI-CDST forensic audit repair items #5 and #6).
    (async () => {
      const { initPassportRecordRetry } = await import("@/lib/passportRecordRetryQueue");
      initPassportRecordRetry();
      const { initVoiceConsentRetry } = await import("@/lib/voiceConsentRetryQueue");
      initVoiceConsentRetry();
      const { initIntakeConsentQueue } = await import('@/lib/consent/intakeConsentQueue');
      initIntakeConsentQueue();
    })();
    // Priority 5: Medication dose offline retry queue
    (async () => {
      const { initMedicationDoseRetry } = await import("@/lib/medicationDoseRetryQueue");
      initMedicationDoseRetry();
    })();

    initSyncOrchestrator();
    
    // Initialize auto-sync for offline eCRF form submissions (page-load sync only)
    const cleanupAutoSync = initializeAutoSync();
    
    return () => {
      cleanupAutoSync();
    };
  }, []);

  // NOTE: document.title is intentionally NOT set here.
  // SiteContext.tsx handles white-label branding (e.g. "Aurum Institute | CRO Portal").
  // react-helmet-async handles per-page SEO titles via <SEOHead>.
  // Setting document.title here would override both, breaking branded subdomains.
  
  // NOTE: QueryClientProvider is already wrapped in main.tsx using @/lib/queryClient.
  // Do NOT wrap again here — that creates nested providers with separate caches.
  return (
    <ErrorBoundary>
      <DataSaverProvider>
        <TooltipProvider delayDuration={300}>
          <Toaster />
          <SonnerToaster position="top-center" richColors closeButton />
          <BrowserRouter>
            <ProductionOptimizations />
            <ScrollToTop />
            <DefaultSEO />
            <LanguagePersistence />
            <Suspense fallback={
              <div className="container mx-auto px-4 py-8 space-y-6">
                <Skeleton className="h-8 w-1/3" />
                <Skeleton className="h-64 w-full" />
              </div>
            }>
              <Routes>
                {/* Emergency logout route */}
                <Route path="/logout" element={<Logout />} />
                
                {/* Core admin redirect */}
                <Route path="/admin" element={<Navigate to="/admin/command-center" replace />} />
                
                {/* Redirects first (for fastest resolution) */}
                {renderRedirects()}
                
                {/* Public routes */}
                {renderRoutes(publicRoutes)}
                
                {/* Protected routes */}
                {renderRoutes(protectedRoutes)}
                
                {/* Admin routes */}
                {renderRoutes(adminRoutes)}
                
                {/* Regional routes */}
                {renderRoutes(regionalRoutes)}
                
                {/* Professional routes */}
                {renderRoutes(professionalRoutes)}
                
                {/* Resource routes */}
                {renderRoutes(resourceRoutes)}

                {/* BRIDGE SERP split — authority surfaces redirected to UmojaReg */}
                {renderUmojaregRedirects()}

                {/* SERP-01 (EDCTP3-funded) routes — being phased out in favor of UmojaReg.
                    Kept until quiet-week sign-off; the prefix redirects above win for /serp/*. */}
                {renderRoutes(serpRoutes)}
                
                
                {/* Vanity URL catch-all for trial site invite slugs (must be before 404) */}
                <Route path="/:siteSlug" element={
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><Skeleton className="h-8 w-8" /></div>}>
                    <VanityInviteRedirect />
                  </Suspense>
                } />

                {/* 404 Page */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <SiteLabelMetaSync />
            <PHCHelpMount />
          </BrowserRouter>
        </TooltipProvider>
      </DataSaverProvider>
    </ErrorBoundary>
  );
};

export default App;
